import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Wallet, Percent, Globe, FileText, Megaphone, Headphones, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import api from "@/lib/api";

const DEFAULT_STORIES = [
  { name: "Rizwan Ahmed", agency: "Al-Safar Travels, Lucknow", quote: "GAMCA appointments that used to take my staff an hour now take 5 minutes. The wallet means no card fumbling — top up once, book all week.", stat: "120+ applications/month" },
  { name: "Priya Nair", agency: "Gulf Gateway Consultants, Kochi", quote: "The white-label tracking page is a game changer. My customers see MY brand, not a third party — it's like having my own processing back-office.", stat: "3x client retention" },
  { name: "Mohd. Farhan", agency: "Desert Route Tours, Hyderabad", quote: "Between the B2B rates and referral commissions, Gulfudaan pays for itself. The monthly PDF statement keeps my accountant happy too.", stat: "₹40k+ saved yearly" },
];

const BENEFITS = [
  { icon: Percent, title: "Wholesale B2B Pricing", text: "Get special agent rates on every service — GAMCA appointments, dummy tickets, visas, packages and more. Your margin, your business." },
  { icon: Wallet, title: "Instant Wallet Payments", text: "Top up your agent wallet (online or offline) and pay for applications in one click — no card entry every time, INR and USD supported." },
  { icon: Globe, title: "White-Label Tracking", text: "Give customers a tracking page with YOUR logo and branding — Gulfudaan stays invisible to your clients." },
  { icon: Briefcase, title: "Referral Commissions", text: "Share your personal referral link — every customer who applies and pays through it earns you a commission straight to your wallet." },
  { icon: FileText, title: "Monthly PDF Statements", text: "Download application and wallet statements any month, ready for your accounts." },
  { icon: Megaphone, title: "Exclusive Offers First", text: "Cashback offers, top-referrer bonuses and announcements land directly on your dashboard and WhatsApp." },
  { icon: Headphones, title: "Dedicated Support", text: "Built-in live chat with the Gulfudaan team, right inside your agent panel." },
];

const STEPS = [
  "Register with your office details and KYC documents (Registration Certificate, Aadhaar, PAN)",
  "Our team verifies and approves your account — usually within 24 hours",
  "Log in, top up your wallet and start creating applications at B2B prices",
];

export default function BecomeAgent() {
  const [stories, setStories] = useState(DEFAULT_STORIES);

  useEffect(() => {
    api.get("/agent-testimonials")
      .then(({ data }) => { if (data.testimonials?.length) setStories(data.testimonials); })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="become-agent-page">
      <SEOHead pageKey="become-agent" />
      <div className="bg-[#1A1A1A] py-16 sm:py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--brand-primary)] mb-3">For Travel Agencies &amp; Consultants</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mb-5 max-w-3xl">Grow your travel business with Gulfudaan's B2B Agent Panel</h1>
          <p className="text-base text-white/60 max-w-2xl leading-relaxed mb-8">
            Wholesale pricing, instant wallet payments, white-label tracking and referral commissions — everything a Gulf-services agency needs, in one panel. Registration is required to access agent pricing.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/agent/register">
              <Button data-testid="become-agent-register-btn" className="bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white h-12 px-8 text-base font-bold">
                Register as an Agent <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/agent/login">
              <Button data-testid="become-agent-login-btn" variant="outline" className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-8 text-base">
                Already an agent? Log in
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <div className="bg-[#FAF8F4] py-14 sm:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading font-bold text-lg text-[#1A1A1A] mb-8">Why agents choose Gulfudaan</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="become-agent-benefits">
            {BENEFITS.map((b) => (
              <div key={b.title} className="rounded-lg border border-[#E5E1DA] bg-white p-6">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#b1976b]/15 mb-3">
                  <b.icon size={18} className="text-[var(--brand-primary)]" />
                </span>
                <p className="font-heading font-bold text-[15px] text-[#1A1A1A] mb-1.5">{b.title}</p>
                <p className="text-[13px] text-[#54595f] leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white py-14 sm:py-20 px-4 border-t border-[#F3F0EA]">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading font-bold text-lg text-[#1A1A1A] mb-8">Agent success stories</h2>
          <div className="grid sm:grid-cols-3 gap-5" data-testid="become-agent-testimonials">
            {stories.map((t) => (
              <div key={t.name} className="rounded-lg border border-[#E5E1DA] bg-white p-6 flex flex-col">
                <p className="text-[13px] text-[#54595f] leading-relaxed flex-1">"{t.quote}"</p>
                <div className="mt-4 pt-4 border-t border-[#F3F0EA]">
                  <p className="font-heading font-bold text-sm text-[#1A1A1A]">{t.name}</p>
                  {t.agency && <p className="text-[11px] text-[#54595f]">{t.agency}</p>}
                  {t.stat && <p className="text-[11px] font-bold text-[var(--brand-primary)] mt-1">{t.stat}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-[#FAF8F4] py-14 sm:py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading font-bold text-lg text-[#1A1A1A] mb-6">How to get started</h2>
          <div className="space-y-4 mb-10" data-testid="become-agent-steps">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-[var(--brand-primary)] mt-0.5 shrink-0" />
                <p className="text-sm text-[#54595f] leading-relaxed"><span className="font-bold text-[#1A1A1A]">Step {i + 1}:</span> {s}</p>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-[color-mix(in_oklab,var(--brand-primary),transparent_70%)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_94%)] p-6 text-center">
            <p className="font-heading font-bold text-base text-[#1A1A1A] mb-2">Agent access requires registration</p>
            <p className="text-[13px] text-[#54595f] mb-4">B2B pricing, wallet and referral commissions are only available to verified registered agents.</p>
            <Link to="/agent/register">
              <Button data-testid="become-agent-cta-bottom" className="bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white h-11 px-8 font-bold">
                Start Registration Now <ArrowRight size={15} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
